import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAppDispatch } from '@/store/hooks';
import { setUser as setReduxUser, clearUser, setLoading as setReduxLoading } from '@/store/userSlice';
import { syncSentryUser } from '@/lib/sentry';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  /**
   * True iff the signed-in user's row in `public.users` has
   * `is_super_admin = true`. Defaults to false until the lookup completes
   * and resets to false on sign-out. Distinct from the per-workspace
   * `users.is_admin` flag that gates `whatsapp_templates`.
   */
  isSuperAdmin: boolean;
  signUp: (email: string, password: string, name: string) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signInWithGoogle: () => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const { toast } = useToast();
  const dispatch = useAppDispatch();

  // Fetch `users.is_super_admin` for the supplied user. Kept out of the
  // useEffect so it can be reused by the initial getSession() branch without
  // duplicating the cast through `unknown` (the generated Supabase types may
  // not list the column yet on a developer's machine — Sergio regenerates
  // them after applying migration 20260513170000).
  const refreshSuperAdminFlag = React.useCallback(async (uid: string | undefined | null) => {
    if (!uid) {
      setIsSuperAdmin(false);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('users')
        .select('is_super_admin')
        .eq('user_id', uid)
        .single();
      if (error) {
        console.error('useAuth: failed to fetch is_super_admin', error);
        setIsSuperAdmin(false);
        return;
      }
      const flag = (data as unknown as { is_super_admin?: boolean } | null)
        ?.is_super_admin;
      setIsSuperAdmin(!!flag);
    } catch (err) {
      console.error('useAuth: is_super_admin lookup threw', err);
      setIsSuperAdmin(false);
    }
  }, []);

  useEffect(() => {
    // Set Redux loading state
    dispatch(setReduxLoading(true));

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Attach the bare minimum identifying info to Sentry events. We
        // intentionally only forward { id, email } — no name, no phone, no
        // workspace_id — to limit PII exposure in error reports. The strip is
        // implemented in the exported `syncSentryUser` helper above so the
        // unit suite can exercise it without rendering React.
        syncSentryUser(Sentry, session);

        setLoading(false);

        // Update Redux store with user data
        dispatch(session?.user ? setReduxUser(session.user) : clearUser());

        // Refresh the super-admin flag for the new identity. On sign-out
        // `session` is null, which resets the flag to false. This runs in
        // the background — pages that need a definitive answer should still
        // use RequireSuperAdmin, which performs its own check.
        if (session?.user) {
          void refreshSuperAdminFlag(session.user.id);
        } else {
          setIsSuperAdmin(false);
        }

        // Handle Google OAuth workspace creation
        if (event === 'SIGNED_IN' && session?.user) {
          setTimeout(() => {
            // Check if user has workspace, if not it will be created by the trigger
            console.log('User signed in:', session.user.email);
          }, 0);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Update Redux store with user data
      dispatch(session?.user ? setReduxUser(session.user) : clearUser());

      // Mirror the onAuthStateChange branch above — needed because the
      // listener doesn't fire for an already-restored session on first load.
      if (session?.user) {
        void refreshSuperAdminFlag(session.user.id);
      } else {
        setIsSuperAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [dispatch, refreshSuperAdminFlag]);

  const signUp = async (email: string, password: string, name: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          name
        }
      }
    });
    
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    return { error };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/`,
      }
    });
    
    return { error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        variant: "destructive",
        title: "Error al cerrar sesión",
        description: error.message,
      });
    } else {
      // Clear user from Redux store on successful sign out
      dispatch(clearUser());
      setIsSuperAdmin(false);
    }
  };

  const value = {
    user,
    session,
    loading,
    isSuperAdmin,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
